// Constants
import { SubRouteType, ViewType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { MyTasksViewPivotType } from "@ms/uno-constants/lib/local/ViewPivots";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
// Utils
import { createRouteFromDetails } from "@ms/uno-routing";
import { DeepLinkSubRouteKeys } from "@ms/uno-routing/lib/local/utilities/DeepLinkUtils";
/**
 * Legacy planner web links for the following scenarios:
 * Url based link to Planner Hub
 * Url based link to My Tasks
 * Url based link to Plan
 * DeepLink to Plan
 * DeepLink to Task
 */ const legacyHubUrlLinkRegex = /^.*\/Home\/Planner\/\#\/planhub$/i;
const legacyMyTasksUrlLinkRegex = /^.*\/Home\/Planner\/#\/(user(?<view>grid|schedule|charts|board)|mytasks)$/i;
const legacyPlanUrlLinkRegex = /^.*\/Home\/Planner\/\#\/plan(?<view>grid|schedule|charts|taskboard|board)\?groupId=(?<containerId>[A-Za-z0-9-_]{28,36})&planId=(?<planId>[A-Za-z0-9-_]{28})$/i;
// Matches a legacy `Home/PlanViews/{planId}/{taskId}?<query>` notification deep link.
// The query string is captured as a whole so its parameters can be read by name (order-independent).
const legacyPlanWithTaskDeepLinkRegex = /\/Home\/PlanViews\/(?<planId>[A-Za-z0-9-_]{28})\/(?<taskId>[A-Za-z0-9-_]{28})\?(?<query>[^#]*)/i;
// Matches a legacy `Home/PlanViews/{planId}?<query>` notification deep link (no task segment).
const legacyPlanDeepLinkRegex = /^.*\/Home\/PlanViews\/(?<planId>[A-Za-z0-9-_]{28})\?(?<query>[^#]*)/i;
// A legacy notification deep link must carry Type, Channel and CreatedTime query parameters.
// These validate the captured query parameter values (parameter order is not significant).
const legacyNotificationTypePattern = /^[A-Za-z]{1,32}$/;
const legacyNotificationChannelPattern = /^[A-Za-z]{1,32}$/;
const legacyNotificationCreatedTimePattern = /^[0-9]{18}$/;
// Matches a legacy `Home/Task/{taskId}?<query>` notification deep link.
// As with PlanViews above, the query string is captured as a whole so its parameters can be
// read by name (order-independent) rather than matched positionally.
const legacyTaskDeepLinkRegex = /^.*\/Home\/Task\/(?<taskId>[A-Za-z0-9-_]{28})\?(?<query>[^#]*)/i;
/**
 * Parses the specified deepLink and returns its corresponding Route (or null, if no valid route parsed)
 * @param deepLink The deepLink to parse
 * @param loggers Loggers to use for logging
 */ export function generateRouteFromLegacyDeepLink(deepLink, loggers) {
    if (!deepLink) {
        return null;
    }
    let linkView;
    let linkPlanId;
    let linkTaskId;
    let linkType;
    let linkChannel;
    let linkCreatedTime;
    let regexResult = null;
    try {
        // Resolving this legacy route requires an authenticated group-to-plan lookup. Fall back to the default or cached route.
        if (deepLink.toLowerCase().includes("/home/group/")) {
            loggers?.traceLogger.logTrace(0x1d7cf84f /* tag_35p7p */ , TraceLevel.Info, "Detected unsupported legacy Planner group route; using default or cached route");
            return null;
        }
        // Case 1: Planner Web Link to Planner Hub
        regexResult = deepLink.match(legacyHubUrlLinkRegex);
        if (regexResult) {
            loggers?.traceLogger.logTrace(0x1e30b505 /* tag_4mluf */ , TraceLevel.Info, `Parsed Planner web link to Planner Hub`);
            return createRouteFromDetails({
                subRouteType: SubRouteType.MyPlans
            });
        }
        // Case 2: Planner Web Link to My Tasks
        regexResult = deepLink.match(legacyMyTasksUrlLinkRegex);
        if (regexResult) {
            linkView = regexResult.groups?.view;
            // Update mapped view based on linkView when we support other view types in ATM
            const mappedView = ViewType.Grid;
            loggers?.traceLogger.logTrace(0x1e30b504 /* tag_4mlue */ , TraceLevel.Info, `Parsed Planner web link to My Tasks [ParsedView=${linkView}][MappedView=${mappedView}]`);
            return createRouteFromDetails({
                subRouteType: SubRouteType.MyTasks,
                myTasksPivot: MyTasksViewPivotType.AssignedToMe,
                view: mappedView
            });
        }
        // Case 3: Planner Web Link to Plan
        regexResult = deepLink.match(legacyPlanUrlLinkRegex);
        if (regexResult) {
            // The following must exist for the regex to match. hence the type assertion
            linkView = regexResult.groups.view;
            linkPlanId = regexResult.groups.planId;
            const mappedView = getViewTypeFromLinkView(linkView);
            loggers?.traceLogger.logTrace(0x1e30b502 /* tag_4mluc */ , TraceLevel.Info, `Parsed legacy planner web link to Plan [ParsedView=${linkView}][MappedView=${mappedView}]`);
            return createRouteFromDetails({
                subRouteType: SubRouteType.BasicPlan,
                planId: linkPlanId,
                view: mappedView
            });
        }
        // Case 4: Planner DeepLink to Plan (with optional task segment)
        // Query parameters (Type/Channel/CreatedTime) may appear in any order and the URL may
        // contain additional interleaved/trailing parameters (e.g. tid), so parse them by name.
        regexResult = deepLink.match(legacyPlanWithTaskDeepLinkRegex) ?? deepLink.match(legacyPlanDeepLinkRegex);
        if (regexResult?.groups) {
            const queryParams = new URLSearchParams(regexResult.groups.query ?? "");
            linkType = getQueryParameterCaseInsensitive(queryParams, "Type");
            linkChannel = getQueryParameterCaseInsensitive(queryParams, "Channel");
            linkCreatedTime = getQueryParameterCaseInsensitive(queryParams, "CreatedTime");
            const isValidNotificationDeepLink = linkType != null && linkChannel != null && linkCreatedTime != null && legacyNotificationTypePattern.test(linkType) && legacyNotificationChannelPattern.test(linkChannel) && legacyNotificationCreatedTimePattern.test(linkCreatedTime);
            if (isValidNotificationDeepLink) {
                linkPlanId = regexResult.groups.planId;
                const maybeTaskId = regexResult.groups.taskId;
                loggers?.traceLogger.logTrace(0x1e30b500 /* tag_4mlua */ , TraceLevel.Info, `Parsed legacy planner deepLink to Plan[LinkType=${linkType}][LinkChannel=${linkChannel}][LinkCreatedTime=${linkCreatedTime}][HasMaybeTask=${!!maybeTaskId}]`);
                const routeDetails = {
                    subRouteType: SubRouteType.BasicPlan,
                    planId: linkPlanId
                };
                if (maybeTaskId) {
                    routeDetails.taskId = maybeTaskId;
                }
                return createRouteFromDetails(routeDetails);
            }
        }
        // Case 5: Planner DeepLink to Task
        // Like Case 4, the notification query parameters (Type/Channel/CreatedTime) may appear in
        // any order and the URL may contain additional interleaved/trailing parameters, so parse
        // them by name rather than by position.
        regexResult = deepLink.match(legacyTaskDeepLinkRegex);
        if (regexResult?.groups) {
            const queryParams = new URLSearchParams(regexResult.groups.query ?? "");
            linkType = getQueryParameterCaseInsensitive(queryParams, "Type");
            linkChannel = getQueryParameterCaseInsensitive(queryParams, "Channel");
            linkCreatedTime = getQueryParameterCaseInsensitive(queryParams, "CreatedTime");
            const isValidNotificationDeepLink = linkType != null && linkChannel != null && linkCreatedTime != null && legacyNotificationTypePattern.test(linkType) && legacyNotificationChannelPattern.test(linkChannel) && legacyNotificationCreatedTimePattern.test(linkCreatedTime);
            if (isValidNotificationDeepLink) {
                linkTaskId = regexResult.groups.taskId;
                loggers?.traceLogger.logTrace(0x1e30b4e2 /* tag_4mlt8 */ , TraceLevel.Info, `Parsed legacy planner deepLink to Task[LinkType=${linkType}][LinkChannel=${linkChannel}][LinkCreatedTime=${linkCreatedTime}]`);
                return createRouteFromDetails({
                    subRouteType: SubRouteType.MyPlans,
                    taskId: linkTaskId
                });
            }
        }
        // No match case
        loggers?.traceLogger.logTrace(0x1e30b4e1 /* tag_4mlt7 */ , TraceLevel.Warning, "generateRouteFromLegacyDeepLink - No routeParams parsed from deepLink");
    } catch (e) {
        loggers?.traceLogger.logTrace(0x1e30b4e0 /* tag_4mlt6 */ , TraceLevel.Warning, "generateRouteFromLegacyDeepLink - Error parsing deepLink");
    }
    return null;
}
/**
 * Parses the specified transformed deepLink and returns its corresponding Route (or null, if no valid route parsed)
 * Note: Legacy Planner Web (todoweb) might turn a link like
 * https://planner.cloud.microsoft/microsoft.onmicrosoft.com/Home/Task/yAx0fYgzok-_0llLXp0IIJUAIJmO
 * into
 * https://planner.cloud.microsoft/microsoft.onmicrosoft.com/en-US/Home/Planner/#/plantaskboard?groupId=95432f65-b837-46f1-9743-439019d647f2&planId=6_Sw5Rux70CzRLkMI6TfL5UAHr_j&taskId=yAx0fYgzok-_0llLXp0IIJUAIJmO"
 * @param deepLinkPath The deepLinkPath to parse
 * @param deepLinkHash The deepLinkHash to parse
 * @param loggers Loggers to use for logging
 */ export function generateRouteFromTransformedLegacyDeepLink(deepLinkPath, deepLinkHash, loggers) {
    if (!deepLinkPath || !deepLinkHash) {
        return null;
    }
    try {
        // If path starts with "/v1/" this is new UNO Web DeepLink schema, skip
        // If path doesn't contain "/Home/Planner" this is not a Legacy Planner Transformed DeepLink, skip
        if (deepLinkPath.startsWith(`/${DeepLinkSubRouteKeys.V1}/`) || !deepLinkPath.toLowerCase().includes("/Home/Planner".toLowerCase())) {
            return null;
        }
        // If hash doesn't start with "#/" then it's not a legacy transformed deepLink to parse
        if (!deepLinkHash.startsWith("#/")) {
            return null;
        }
        // Remove the leading "#/" from the hash
        deepLinkHash = deepLinkHash.substring(2);
        // Split deepLinkHash into the pageType and query parameters (if present)
        const deepLinkHashSegments = deepLinkHash.split("?");
        const pageType = deepLinkHashSegments[0]?.toLowerCase() ?? "";
        const queryParams = deepLinkHashSegments.length > 0 ? deepLinkHashSegments[1] : "";
        // [Case 1] Planner Hub
        if (pageType === "planhub") {
            loggers?.traceLogger.logTrace(0x1e2da258 /* tag_4l0jy */ , TraceLevel.Info, `Parsed Planner web link to Planner Hub`);
            return createRouteFromDetails({
                subRouteType: SubRouteType.MyPlans
            });
        }
        // [Case 2] My Tasks
        const myTasksPageTypes = [
            "userboard",
            "usergrid",
            "usercharts",
            "userschedule",
            "mytasks"
        ];
        if (myTasksPageTypes.includes(pageType)) {
            loggers?.traceLogger.logTrace(0x1e2da257 /* tag_4l0jx */ , TraceLevel.Info, `Parsed Planner web link to My Tasks [ParsedView=${pageType}]`);
            return createRouteFromDetails({
                subRouteType: SubRouteType.MyTasks,
                myTasksPivot: MyTasksViewPivotType.AssignedToMe
            });
        }
        // [Case 3] Plan
        const myPlanPageTypes = [
            "plantaskboard",
            "plangrid",
            "plancharts",
            "planschedule",
            "planboard"
        ];
        if (myPlanPageTypes.includes(pageType)) {
            const pageTypeView = pageType.replace(/^plan/, "");
            const view = getViewTypeFromLinkView(pageTypeView);
            const searchParams = new URLSearchParams(queryParams);
            const planId = searchParams.get("planId");
            const taskId = searchParams.get("taskId");
            if (planId) {
                loggers?.traceLogger.logTrace(0x1e2da256 /* tag_4l0jw */ , TraceLevel.Info, `Parsed legacy planner web link to Plan [ParsedView=${pageType}][HasTask=${!!taskId}]`);
                const basicPlanRouteCreateDetails = {
                    subRouteType: SubRouteType.BasicPlan,
                    planId: planId,
                    view: view
                };
                if (taskId) {
                    basicPlanRouteCreateDetails.taskId = taskId;
                }
                return createRouteFromDetails(basicPlanRouteCreateDetails);
            }
        }
        // Nothing parsed
        loggers?.traceLogger.logTrace(0x1e2da255 /* tag_4l0jv */ , TraceLevel.Warning, "generateRouteFromTransformedLegacyDeepLink - No routeParams parsed from deepLink");
    } catch (e) {
        loggers?.traceLogger.logTrace(0x1e2da254 /* tag_4l0ju */ , TraceLevel.Warning, "generateRouteFromTransformedLegacyDeepLink - Error parsing deepLink");
    }
    return null;
}
/**
 * Gets the ViewType from the specified linkView
 * @param linkView The linkView to parse
 */ function getViewTypeFromLinkView(linkView) {
    switch(linkView){
        case "taskboard":
        case "board":
            return ViewType.Board;
        case "charts":
            return ViewType.Charts;
        case "schedule":
            return ViewType.Schedule;
        case "grid":
        default:
            return ViewType.Grid;
    }
}
/**
 * Reads a query parameter value by name, ignoring case.
 * Legacy notification links are not guaranteed to use a fixed casing for parameter keys.
 * @param queryParams The parsed query parameters
 * @param key The parameter name to read
 */ function getQueryParameterCaseInsensitive(queryParams, key) {
    const lowerKey = key.toLowerCase();
    return [
        ...queryParams
    ].find(([paramKey])=>paramKey.toLowerCase() === lowerKey)?.[1];
}
